<script lang="ts">
    import type { GridItemHTMLElement, GridStack } from "gridstack";

    import { afterUpdate } from "svelte";

    import GridStackBoard from "./GridStackBoard.svelte";
    import type { GridStackEvent } from "./GridStackBoard.svelte";
    import type { Colour, TileType, GridStackTile } from "../types/tile";
    import { ColourToString, TileTypeToString } from "../misc/boardTypes";

    let gridStackBoard: any;
    const getTableGrid = () => gridStackBoard.getTableGrid();
    const getTrayGrid = () => gridStackBoard.getTrayGrid();

    export let tableTiles: GridStackTile[] = [];
    export let trayTiles: GridStackTile[] = [];

    export let canMoveTableTile: (e: GridStackEvent) => Promise<boolean> = () =>
        Promise.resolve(true);
    export let canAddTile: (e: GridStackEvent) => Promise<boolean> = () =>
        Promise.resolve(true);
    export let canRemoveTile: (e: GridStackEvent) => Promise<boolean> = () =>
        Promise.resolve(true);

    export let enableTableTileMovement = true;
    export let showTableChrome = true;
    export let showTrayHelp = true;

    afterUpdate(() => {
        if (!gridStackBoard) return;

        refreshGrids();
        getTableGrid().enableMove(enableTableTileMovement);
    });

    // ---------------------
    // --* EVENT HANDLERS
    // ---------------------

    const onMoveTableTile = async (e: CustomEvent<GridStackEvent>) => {
        const { src, dst } = e.detail;
        disableGrids();

        const shouldMoveTableTile = await canMoveTableTile(e.detail);
        if (shouldMoveTableTile) {
            moveTile(tableTiles, src.x, src.y, dst.x, dst.y);
        } else {
            refreshGrids();
        }

        enableGrids();
    };

    const onMoveTrayTile = (e: CustomEvent<GridStackEvent>) => {
        const { src, dst } = e.detail;
        moveTile(trayTiles, src.x, src.y, dst.x, dst.y);
    };

    const onAddTile = async (e: CustomEvent<GridStackEvent>) => {
        disableGrids();

        const shouldAddTile = await canAddTile(e.detail);
        if (shouldAddTile) {
            updateTileLists();
        } else {
            refreshGrids();
        }

        enableGrids();
    };

    const onRemoveTile = async (e: CustomEvent<GridStackEvent>) => {
        disableGrids();

        const shouldRemoveTile = await canRemoveTile(e.detail);
        if (shouldRemoveTile) {
            updateTileLists();
        } else {
            refreshGrids();
        }

        enableGrids();
    };

    // ----------------
    // --* FUNCTIONS
    // ----------------

    const updateTileLists = () => {
        tableTiles = getTileListFromGrid(getTableGrid());
        trayTiles = getTileListFromGrid(getTrayGrid());
    };

    const getTileListFromGrid = (grid: GridStack) => {
        let tileList: GridStackTile[] = [];

        const gridItems = grid.getGridItems();
        for (const gridItem of gridItems) {
            const tile = gridItemToTile(gridItem);
            tileList = [...tileList, tile];
        }

        return tileList;
    };

    const gridItemToTile = (gridItem: GridItemHTMLElement): GridStackTile => {
        const itemColourStr = gridItem.getAttribute("colour");
        const itemTileTypeStr = gridItem.getAttribute("tileType");

        const itemColour = parseInt(itemColourStr) as Colour;
        const itemTileType = parseInt(itemTileTypeStr) as TileType;
        const itemIsStaging = gridItem.children[0].className.includes(
            "staging-tile"
        );

        return {
            x: gridItem.gridstackNode.x,
            y: gridItem.gridstackNode.y,
            colour: itemColour,
            tileType: itemTileType,
            staging: itemIsStaging,
        };
    };

    const moveTile = (
        tiles: GridStackTile[],
        srcX: number,
        srcY: number,
        dstX: number,
        dstY: number
    ) => {
        for (let tile of tiles) {
            if (tile.x === srcX && tile.y === srcY) {
                tile.x = dstX;
                tile.y = dstY;
            }
        }
    };

    const refreshGrids = () => {
        refreshGrid(getTableGrid(), tableTiles);
        refreshGrid(getTrayGrid(), trayTiles);
    };

    const refreshGrid = (grid: GridStack, tiles: GridStackTile[]) => {
        grid.removeAll();

        for (const tile of tiles) {
            addGridStackTileToGrid(tile, grid);
        }
    };

    const addGridStackTileToGrid = (tile: GridStackTile, grid: GridStack) => {
        const colourStr = ColourToString(tile.colour);
        const tileTypeStr = TileTypeToString(tile.tileType);
        const tileDisplayStr = tile.tileType === 0 ? "☺" : tileTypeStr;

        const colourClass = `tile-${colourStr}`;
        const legacyColourClass = `${colourStr}-tile`;
        const stagingClass = tile.staging ? "staging-tile" : "";
        const jokerClass = tile.tileType === 0 ? "joker-tile" : "";
        const smileyClass = tile.tileType === 0 ? "smiley-symbol" : "";

        const item = grid.addWidget(
            `<div class="grid-stack-item">
                <div class="grid-stack-item-content tile-face ${colourClass} ${stagingClass} ${jokerClass}" draggable="true">
                    <span class="value-area">
                        <span class="symbol ${legacyColourClass} ${smileyClass}">${tileDisplayStr}</span>
                    </span>
                </div>
            </div>`,
            {
                x: tile.x,
                y: tile.y,
                w: 1,
                h: 1,
                locked: true,
            }
        );

        item.setAttribute("colour", "" + tile.colour);
        item.setAttribute("tileType", "" + tile.tileType);
    };

    const enableGrids = () => {
        getTableGrid().enableMove(true);
        getTrayGrid().enableMove(true);
    };

    const disableGrids = () => {
        getTableGrid().enableMove(false);
        getTrayGrid().enableMove(false);
    };
</script>

<GridStackBoard
    bind:this={gridStackBoard}
    {showTableChrome}
    {showTrayHelp}
    on:moveTableTile={onMoveTableTile}
    on:moveTrayTile={onMoveTrayTile}
    on:addTile={onAddTile}
    on:removeTile={onRemoveTile}
/>
